import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import { useQueryClient } from '@tanstack/react-query';
import SessionsTable from '../components/SessionsTable';
import OnchainTransactionsTable from '../components/OnchainTransactionsTable';
import EventsTable from '../components/EventsTable';
import ActivityContractSelector from '../../../shared/components/analytics/ActivityContractSelector';
import TransactionsFilterBar from '../components/TransactionsFilterBar';
import TransactionColumnConfigModal from '../components/TransactionColumnConfigModal';
import SessionsFilterBar from '../components/SessionsFilterBar';
import SessionColumnConfigModal from '../components/SessionColumnConfigModal';
import UsersFilterBar from '../components/UsersFilterBar';
import UsersTable from '../components/UsersTable';
import UserColumnConfigModal from '../components/UserColumnConfigModal';
import GroupsFilterBar from '../components/GroupsFilterBar';
import GroupsTable from '../components/GroupsTable';
import GroupColumnConfigModal from '../components/GroupColumnConfigModal';
import { FIXED_GROUP_COLUMNS } from '../config/groupColumns';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import { usePreferences } from '../../../shared/context/preferencesContext';
import { RefreshCw } from 'lucide-react';

const Sessions = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Get chain context from URL params, default to 'offchain'
  const subpage = params.subpage || 'offchain';
  const chainContext = subpage === 'onchain' ? 'Onchain' : (subpage === 'events' ? 'Events' : (subpage === 'users' ? 'Users' : 'Offchain'));
  
  // Offchain state
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [eventsFilters, setEventsFilters] = useState([]);
  const [appliedEventsFilters, setAppliedEventsFilters] = useState([]);
  
  // Onchain state
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [selectedContracts, setSelectedContracts] = useState([]);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [modularFiltersUsers, setModularFiltersUsers] = useState([]);
  const [appliedModularFiltersUsers, setAppliedModularFiltersUsers] = useState([]);
  const [userTimeFilter, setUserTimeFilter] = useState(null);
  const [userSelectedTimePreset, setUserSelectedTimePreset] = useState('30d');
  const [lastUserRefreshTime, setLastUserRefreshTime] = useState(null);
  const [isUserColumnModalOpen, setIsUserColumnModalOpen] = useState(false);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);

  // Group tabs state
  const [groupKeys, setGroupKeys] = useState([]); // [{group_key, count}]
  const [activeGroupTab, setActiveGroupTab] = useState(null); // null = Users tab
  const [groups, setGroups] = useState([]);
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupTimeFilter, setGroupTimeFilter] = useState(null);
  const [groupSelectedTimePreset, setGroupSelectedTimePreset] = useState('30d');
  const [groupModularFilters, setGroupModularFilters] = useState([]);
  const [appliedGroupModularFilters, setAppliedGroupModularFilters] = useState([]);
  const [loadingMoreGroups, setLoadingMoreGroups] = useState(false);
  const [isGroupColumnModalOpen, setIsGroupColumnModalOpen] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [lastGroupRefreshTime, setLastGroupRefreshTime] = useState(null);
  /** Distinct custom_properties keys for the active group_key (from API + merged locally) */
  const [groupCustomPropertyKeysFromApi, setGroupCustomPropertyKeysFromApi] = useState([]);
  
  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [modularFilters, setModularFilters] = useState([]);
  const [appliedModularFilters, setAppliedModularFilters] = useState([]);
  // Onchain filter state
  const [onchainFilters, setOnchainFilters] = useState([]);
  const [appliedOnchainFilters, setAppliedOnchainFilters] = useState([]);
  const [contractPropertyFilters, setContractPropertyFilters] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem("selectedTeam") || '');

  // ---------------------------------------------------------------------------
  // Back/forward UX: persist Users/Groups view state in the current history entry
  // and hydrate it on mount. React Query keeps the DATA cache; this keeps the UI
  // controls (tab, search, filters, time ranges) stable across navigation.
  // ---------------------------------------------------------------------------

  const restoringFromHistoryRef = useRef(false);

  // Hydrate from history state when returning to /activity/users
  useEffect(() => {
    if (chainContext !== 'Users') return;
    // Prefer history state (popstate/back-forward) over location.state
    const histUsr =
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state.usr
        : null;
    const st = (histUsr && typeof histUsr === 'object')
      ? histUsr
      : (location.state && typeof location.state === 'object' ? location.state : null);

    const saved = st?.activityUsersState;
    if (!saved || typeof saved !== 'object') return;

    restoringFromHistoryRef.current = true;
    try {
      if (typeof saved.activeGroupTab !== 'undefined') setActiveGroupTab(saved.activeGroupTab);
      if (typeof saved.searchTerm === 'string') setSearchTerm(saved.searchTerm);
      if (typeof saved.groupSearchTerm === 'string') setGroupSearchTerm(saved.groupSearchTerm);

      if (saved.userTimeFilter) setUserTimeFilter(saved.userTimeFilter);
      if (typeof saved.userSelectedTimePreset === 'string') setUserSelectedTimePreset(saved.userSelectedTimePreset);
      if (Array.isArray(saved.modularFiltersUsers)) setModularFiltersUsers(saved.modularFiltersUsers);
      if (Array.isArray(saved.appliedModularFiltersUsers)) setAppliedModularFiltersUsers(saved.appliedModularFiltersUsers);

      if (saved.groupTimeFilter) setGroupTimeFilter(saved.groupTimeFilter);
      if (typeof saved.groupSelectedTimePreset === 'string') setGroupSelectedTimePreset(saved.groupSelectedTimePreset);
      if (Array.isArray(saved.groupModularFilters)) setGroupModularFilters(saved.groupModularFilters);
      if (Array.isArray(saved.appliedGroupModularFilters)) setAppliedGroupModularFilters(saved.appliedGroupModularFilters);
    } finally {
      // Let state settle first, then allow persistence to resume.
      setTimeout(() => {
        restoringFromHistoryRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext]);

  // Persist current view state into this history entry WITHOUT navigation.
  // Using `navigate(..., { replace: true })` can remount ActivityPage and cause the
  // "Loading websites..." flash because ActivityPage re-runs its mount effect.
  useEffect(() => {
    if (chainContext !== 'Users') return;
    if (restoringFromHistoryRef.current) return;
    try {
      const current = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};

      // React Router stores its own state structure; we only merge in our slice.
      const next = {
        ...current,
        usr: {
          ...(current.usr && typeof current.usr === 'object' ? current.usr : {}),
          ...(location.state && typeof location.state === 'object' ? location.state : {}),
          activityUsersState: {
            activeGroupTab,
            searchTerm,
            groupSearchTerm,
            userTimeFilter,
            userSelectedTimePreset,
            modularFiltersUsers,
            appliedModularFiltersUsers,
            groupTimeFilter,
            groupSelectedTimePreset,
            groupModularFilters,
            appliedGroupModularFilters,
          },
        },
      };

      window.history.replaceState(next, document.title);
    } catch (_) {
      // Ignore history write failures (Safari private mode etc.)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chainContext,
    activeGroupTab,
    searchTerm,
    groupSearchTerm,
    userTimeFilter,
    userSelectedTimePreset,
    modularFiltersUsers,
    appliedModularFiltersUsers,
    groupTimeFilter,
    groupSelectedTimePreset,
    groupModularFilters,
    appliedGroupModularFilters,
  ]);
  // Transaction time filter (for Onchain) and edit columns modal
  const [transactionTimeFilter, setTransactionTimeFilter] = useState(null);
  const [transactionSelectedTimePreset, setTransactionSelectedTimePreset] = useState('today');
  const [lastTransactionRefreshTime, setLastTransactionRefreshTime] = useState(null);
  const [isTransactionColumnModalOpen, setIsTransactionColumnModalOpen] = useState(false);
  const [loadingMoreTransactions, setLoadingMoreTransactions] = useState(false);
  // Activity Sessions (Offchain) time filter and UI
  const [sessionTimeFilter, setSessionTimeFilter] = useState(null);
  const [sessionSelectedTimePreset, setSessionSelectedTimePreset] = useState('today');
  const [lastSessionRefreshTime, setLastSessionRefreshTime] = useState(null);
  const [isSessionColumnModalOpen, setIsSessionColumnModalOpen] = useState(false);
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);

  const {
    loadTransactionsTableColumns,
    getTransactionsTableColumns,
    loadSessionsTableColumns,
    getSessionsTableColumns,
    loadUsersTableColumns,
    getUsersTableColumns,
    getGroupsTableColumns
  } = usePreferences();

  // teamId for Activity APIs (PostgreSQL team id or name from selectedTeamData)
  const teamId = useMemo(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem("selectedTeam") || "";
    } catch (e) {
      return localStorage.getItem("selectedTeam") || "";
    }
  }, []);

  const fixedGroupColumnKeySet = useMemo(() => new Set(Object.keys(FIXED_GROUP_COLUMNS)), []);

  const fetchGroupCustomPropertyKeys = useCallback(async () => {
    if (!teamId || !activeGroupTab) {
      setGroupCustomPropertyKeysFromApi([]);
      return;
    }
    try {
      const r = await axiosInstance.get('/group-identities/properties', {
        params: { team_id: teamId, group_key: activeGroupTab }
      });
      if (r.data?.success && Array.isArray(r.data.properties)) {
        setGroupCustomPropertyKeysFromApi(r.data.properties);
      } else {
        setGroupCustomPropertyKeysFromApi([]);
      }
    } catch (err) {
      console.error('Error fetching group custom property keys:', err);
      setGroupCustomPropertyKeysFromApi([]);
    }
  }, [teamId, activeGroupTab]);

  /** Keys offered in Edit columns + used for table headers (API ∪ current rows ∪ saved prefs) */
  const groupAvailableCustomProps = useMemo(() => {
    const s = new Set();
    (groupCustomPropertyKeysFromApi || []).forEach((item) => {
      const k = typeof item === 'string' ? item : item?.key;
      if (k) s.add(k);
    });
    (groups || []).forEach((g) => {
      Object.keys(g.customProperties || {}).forEach((k) => s.add(k));
    });
    if (activeGroupTab) {
      const cfg = getGroupsTableColumns(activeGroupTab);
      [...(cfg.enabled || []), ...(cfg.order || [])].forEach((k) => {
        if (!fixedGroupColumnKeySet.has(k)) s.add(k);
      });
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [
    groupCustomPropertyKeysFromApi,
    groups,
    activeGroupTab,
    getGroupsTableColumns,
    fixedGroupColumnKeySet
  ]);
  
  // Track latest request and cancel in-flight ones to avoid stale updates
  const latestRequestRef = useRef(0);
  const abortControllerRef = useRef(null);
  const sessionIdSearchRef = useRef(null); // Track if we've already searched for a sessionId
  const userIdSearchRef = useRef(null); // Track if we've already searched for a userId

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    if (chainContext === 'Offchain') {
      fetchSessionsWithDateRange(1, term);
    } else if (chainContext === 'Events') {
      fetchEvents(1, term, appliedEventsFilters);
    } else if (chainContext === 'Users') {
      fetchUsersWithDateRange(1, term, null, true);
    } else {
      fetchTransactionsWithDateRange(1, true, term, null, null);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (chainContext === 'Onchain' || chainContext === 'Offchain') {
      // Transactions and Sessions use Load More, not page change
      return;
    }
    if (chainContext === 'Events') {
      fetchEvents(page, null, appliedEventsFilters);
    }
  };

  const handleTransactionLoadMore = () => {
    if (!loadingMoreTransactions && currentPage < totalPages && teamId && transactionTimeFilter) {
      fetchTransactionsWithDateRange(currentPage + 1, false);
    }
  };

  const handleSessionLoadMore = () => {
    if (!loadingMoreSessions && currentPage < totalPages && teamId && sessionTimeFilter) {
      fetchSessionsWithDateRange(currentPage + 1, null, null, false);
    }
  };

  const handleUserLoadMore = () => {
    if (!loadingMoreUsers && currentPage < totalPages && userTimeFilter) {
      fetchUsersWithDateRange(currentPage + 1, null, null, false);
    }
  };

  const handleGroupLoadMore = () => {
    if (!loadingMoreGroups && currentPage < totalPages && groupTimeFilter && activeGroupTab) {
      fetchGroupsWithDateRange(currentPage + 1, null, null, false);
    }
  };

  // Groups filter bar: time filter change (uses updated_at)
  const handleGroupTimeFilterChange = (filter) => {
    setGroupTimeFilter(filter);
    setGroupSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setGroups([]);
    setTotalGroups(0);
  };

  const handleGroupRefresh = () => {
    setLastGroupRefreshTime(new Date());
    setCurrentPage(1);
    if (activeGroupTab && groupTimeFilter) fetchGroupsWithDateRange(1, null, null, true);
  };

  const handleGroupSearchChange = (term) => {
    setGroupSearchTerm(term);
    setCurrentPage(1);
    if (activeGroupTab && groupTimeFilter) fetchGroupsWithDateRange(1, term, null, true);
  };

  const handleGroupRowClick = (group) => {
    const team = params.team || localStorage.getItem('selectedTeam') || '';
    navigate(
      `/${encodeURIComponent(team)}/activity/groups/${encodeURIComponent(group.groupKey)}/${encodeURIComponent(group.groupId)}`
    );
  };

  const handleGroupTabChange = (groupKey) => {
    setActiveGroupTab(groupKey);
    setGroups([]);
    setTotalGroups(0);
    setCurrentPage(1);
    setGroupSearchTerm('');
    setGroupModularFilters([]);
    setAppliedGroupModularFilters([]);
  };

  const handleGroupModularFiltersChange = (filters) => {
    setGroupModularFilters(filters);
    const noValueOps = ['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'];
    const validFilters = (filters || []).filter((f) => {
      const src = f.propertySource || f.category;
      if (src === 'cohort') {
        const ids = Array.isArray(f.value) ? f.value : f.value != null ? [f.value] : [];
        return ids.length > 0 && (f.field === 'cohort' || f.property === 'cohort' || ids[0] != null);
      }
      if (!(f.field || f.property) || !f.operator) return false;
      if (!noValueOps.includes(f.operator)) {
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if ((f.operator === 'between' || f.operator === 'not_between') && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      }
      return true;
    });
    setAppliedGroupModularFilters(validFilters);
    setCurrentPage(1);
    // Refetch whenever filters change, even if group time preset has not initialized yet.
    if (activeGroupTab) fetchGroupsWithDateRange(1, null, null, true, validFilters);
  };

  // Users filter bar: time filter change (uses last_visit)
  const handleUserTimeFilterChange = (filter) => {
    setUserTimeFilter(filter);
    setUserSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setUsers([]);
    setTotalUsers(0);
    // Intentionally no direct fetch here — setUserTimeFilter triggers the
    // "Initial data fetch for Users" useEffect which fetches with the new filter,
    // avoiding a double-fetch / "canceled" race condition.
  };

  // Users filter bar: refresh
  const handleUserRefresh = () => {
    setLastUserRefreshTime(new Date());
    setCurrentPage(1);
    fetchUsersWithDateRange(1, null, null, true);
  };

  // Navigate to user detail page when row is clicked
  const handleUserRowClick = (user) => {
    const uid = user.distinctId || user.userId;
    if (!uid) return;
    const team = params.team || localStorage.getItem('selectedTeam') || '';
    // Prefer the user's own websiteId (populated from the list response) so that
    // clicking a user from a different website than the currently-selected one
    // still resolves to the correct identity on the detail page.
    navigate(`/${encodeURIComponent(team)}/activity/users/${encodeURIComponent(uid)}`, {
      state: { websiteId: user.websiteId }
    });
  };

  // Handle userId query param - navigate to user detail page when users load
  useEffect(() => {
    const userIdFromUrl = new URLSearchParams(location.search).get('userId');
    if (!userIdFromUrl || chainContext !== 'Users' || !users.length || loading) return;
    const matching = users.find(u =>
      (u.distinctId || u.userId) === userIdFromUrl ||
      (u.distinctId || u.userId)?.toLowerCase() === userIdFromUrl?.toLowerCase()
    );
    if (matching) {
      const uid = matching.distinctId || matching.userId;
      const team = params.team || localStorage.getItem('selectedTeam') || '';
      navigate(`/${encodeURIComponent(team)}/activity/users/${encodeURIComponent(uid)}`, {
        state: { websiteId: matching.websiteId },
        replace: true
      });
    }
  }, [chainContext, users, loading, location.search, params.team, navigate]);

  // Handle URL changes - navigate when chain context should change
  useEffect(() => {
    // If we're on /activity without a subpage, redirect to offchain
    if (location.pathname.endsWith('/activity') && !params.subpage) {
      const team = params.team || localStorage.getItem("selectedTeam") || '';
      navigate(`/${team}/activity/offchain`, { replace: true });
    }
  }, [location.pathname, params.subpage, params.team, navigate]);

  // Handle sessionId query parameter - filter to show only that session
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionIdParam = searchParams.get('sessionId');
    
    if (sessionIdParam && chainContext === 'Offchain' && teamId && !loading) {
      if (sessionIdSearchRef.current !== sessionIdParam || searchTerm !== sessionIdParam) {
        sessionIdSearchRef.current = sessionIdParam;
        setSearchTerm(sessionIdParam);
        setTimeout(() => {
          fetchSessionsWithDateRange(1, sessionIdParam);
        }, 0);
      }
    } else if (!sessionIdParam) {
      // Clear the ref when sessionId param is removed
      sessionIdSearchRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, chainContext, teamId, loading]);

  // Handle search query parameter for Events view
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchParam = searchParams.get('search');
    
    if (searchParam && chainContext === 'Events' && teamId && !loading) {
      if (searchTerm !== searchParam) {
        setSearchTerm(searchParam);
        setTimeout(() => {
          fetchEvents(1, searchParam, appliedEventsFilters);
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, chainContext, teamId, loading]);

  // Handle search query parameter for Onchain view (transactions)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchParam = searchParams.get('search');
    
    if (searchParam && chainContext === 'Onchain' && !loading) {
      // Wait for contracts to be loaded before searching
      if (selectedContracts.length > 0) {
        // Only trigger search if we haven't already searched for this term
        if (searchTerm !== searchParam) {
          setSearchTerm(searchParam);
          // Use a small delay to ensure state is updated before API call
          setTimeout(() => {
            fetchTransactionsWithDateRange(1, true, searchParam, null, null);
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, chainContext, selectedContracts, loading]);

  // Handle userId query parameter - filter to show only that user (for Users view)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userIdParam = searchParams.get('userId');
    
    if (userIdParam && chainContext === 'Users' && teamId) {
      if (userIdSearchRef.current !== userIdParam) {
        userIdSearchRef.current = userIdParam;
      }
    } else if (!userIdParam) {
      userIdSearchRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, chainContext, teamId]);
  
  // Reset state when chain context changes (via URL)
  useEffect(() => {
    setCurrentPage(1);
    // Don't reset searchTerm if we have query parameters in URL (to preserve the filter)
    const searchParams = new URLSearchParams(location.search);
    const sessionIdParam = searchParams.get('sessionId');
    const userIdParam = searchParams.get('userId');
    const searchParam = searchParams.get('search');
    if (!sessionIdParam && !userIdParam && !searchParam) {
      setSearchTerm('');
      sessionIdSearchRef.current = null;
      userIdSearchRef.current = null;
    }
    setError(null);
    setLoading(false);
    
    if (chainContext === 'Offchain') {
      if (teamId) {
        if (sessionIdParam) {
          fetchSessionsWithDateRange(1, sessionIdParam);
        } else {
          fetchSessionsWithDateRange(1);
        }
      } else {
        setSessions([]);
        setTotalSessions(0);
        setTotalPages(1);
      }
    } else if (chainContext === 'Events') {
      if (teamId) {
        const searchParams = new URLSearchParams(location.search);
        const searchParam = searchParams.get('search');
        if (searchParam) {
          setSearchTerm(searchParam);
          fetchEvents(1, searchParam, appliedEventsFilters);
        } else {
          fetchEvents(1, null, appliedEventsFilters);
        }
      } else {
        setEvents([]);
        setTotalEvents(0);
        setTotalPages(1);
      }
    } else if (chainContext === 'Onchain') {
      // Switch to transactions view
      if (selectedContracts.length > 0) {
        // Check for search parameter in URL
        const searchParams = new URLSearchParams(location.search);
        const searchParam = searchParams.get('search');
        if (searchParam) {
          setSearchTerm(searchParam);
          fetchTransactionsWithDateRange(1, true, searchParam, null, null);
        } else {
          fetchTransactionsWithDateRange(1, true);
        }
      } else {
        // Reset state if no sites selected
        setEvents([]);
        setTotalEvents(0);
        setTotalPages(1);
      }
    } else if (chainContext === 'Users') {
      setUsers([]);
      setTotalUsers(0);
    } else {
      // Switch to transactions view
      if (selectedContracts.length > 0) {
        fetchTransactionsWithDateRange(1, true);
      } else {
        // Reset state if no contracts selected
        setTransactions([]);
        setTotalTransactions(0);
        setTotalPages(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext]);
  
  // Handle contract selection (for Onchain view: filter by contracts or show all team transactions)
  const handleContractChange = (contracts) => {
    setSelectedContracts(contracts);
    setCurrentPage(1);
    if (teamId && transactionTimeFilter) {
      fetchTransactionsWithDateRange(1, true);
    } else if (contracts.length === 0) {
      setTransactions([]);
      setTotalTransactions(0);
      setTotalPages(1);
      setLoading(false);
    }
  };

  // Transaction filter bar: time filter change
  const handleTransactionTimeFilterChange = (filter) => {
    setTransactionTimeFilter(filter);
    setTransactionSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setTransactions([]);
    setTotalTransactions(0);
    fetchTransactionsWithDateRange(1, true, null, null, filter);
  };

  // Transaction filter bar: refresh
  const handleTransactionRefresh = () => {
    setLastTransactionRefreshTime(new Date());
    setCurrentPage(1);
    if (selectedContracts.length > 0) {
      fetchTransactionsWithDateRange(1, true);
    }
  };

  // Activity Sessions filter bar: time filter change (uses start_time)
  const handleSessionTimeFilterChange = (filter) => {
    setSessionTimeFilter(filter);
    setSessionSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentPage(1);
    setSessions([]);
    setTotalSessions(0);
    if (teamId) {
      fetchSessionsWithDateRange(1, null, filter);
    }
  };

  // Activity Sessions filter bar: refresh
  const handleSessionRefresh = () => {
    setLastSessionRefreshTime(new Date());
    setCurrentPage(1);
    if (teamId) {
      fetchSessionsWithDateRange(1);
    }
  };

  // Fetch events for Events view
  const fetchEvents = async (page = 1, searchOverride = null, filtersOverride = null) => {
    if (!teamId) {
      setEvents([]);
      setTotalEvents(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }
    
    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        team_id: teamId,
        page: page.toString(),
        limit: '25'
      });

      // Add search term
      const currentSearchTerm = searchOverride !== null ? searchOverride : searchTerm;
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.set('search', currentSearchTerm.trim());
      }

      // Add filters
      const currentFilters = filtersOverride !== null ? filtersOverride : appliedEventsFilters;
      if (currentFilters && currentFilters.length > 0) {
        const filtersJson = JSON.stringify(currentFilters);
        params.set('modularFilters', filtersJson);
      }

      const response = await axiosInstance.get(`/events?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });

      // Ignore if a newer request has been issued
      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (response.data.success) {
        const eventsData = response.data.events || [];
        const pagination = response.data.pagination || {};
        
        setEvents(eventsData);
        setTotalPages(pagination.totalPages || 1);
        setCurrentPage(pagination.page || page);
        setTotalEvents(pagination.total || 0);
      } else {
        setEvents([]);
        setTotalPages(1);
        setTotalEvents(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching events:', err);
      setError(err.response?.data?.error || 'Failed to fetch events');
      setEvents([]);
      setTotalPages(1);
      setTotalEvents(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };
  
  // Build from/to for sessions API (start_time filter)
  const getSessionTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : sessionTimeFilter;
    if (!filterToUse) return {};
    const fromDate = new Date(filterToUse.from);
    const toDate = new Date(filterToUse.to);
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Build from/to for groups API (updated_at filter)
  const getGroupsTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : groupTimeFilter;
    if (!filterToUse) return {};
    return {
      from: new Date(filterToUse.from).toISOString(),
      to: new Date(filterToUse.to).toISOString()
    };
  };

  // Build from/to for users API (last_visit filter)
  const getUsersTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : userTimeFilter;
    if (!filterToUse) return {};
    const fromDate = new Date(filterToUse.from);
    const toDate = new Date(filterToUse.to);
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Build startDate/endDate from transaction time filter for API
  const getTransactionTimeRangeParams = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : transactionTimeFilter;
    if (!filterToUse) return {};
    const fromDate = new Date(filterToUse.from);
    const toDate = new Date(filterToUse.to);
    return {
      startDate: fromDate.toISOString(),
      endDate: toDate.toISOString()
    };
  };

  // Fetch transactions for Onchain view (team-level). isReset = replace list; !isReset = append (Load More).
  const fetchTransactionsWithDateRange = async (page = 1, isReset = true, searchOverride = null, filtersOverride = null, timeFilterOverride = null) => {
    if (!teamId) {
      setTransactions([]);
      setTotalTransactions(0);
      setTotalPages(1);
      setLoading(false);
      setLoadingMoreTransactions(false);
      return;
    }

    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMoreTransactions(true);
      }

      const currentSearchTerm = searchOverride !== null ? searchOverride : searchTerm;
      const fieldOrProp = (f) => f.field || f.property;
      const validOnchain = (onchainFilters || []).filter(f => {
        if (!fieldOrProp(f) || !f.operator) return false;
        if (['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'].includes(f.operator)) return true;
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if (f.operator === 'between' && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
        return true;
      });
      const currentFilters = filtersOverride !== null
        ? filtersOverride
        : [...validOnchain, ...(contractPropertyFilters || [])];

      const params = {
        page: page.toString(),
        limit: '25'
      };

      const timeRange = getTransactionTimeRangeParams(timeFilterOverride);
      if (timeRange.startDate) params.startDate = timeRange.startDate;
      if (timeRange.endDate) params.endDate = timeRange.endDate;

      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.search = currentSearchTerm.trim();
      }

      if (currentFilters && currentFilters.length > 0) {
        params.modularFilters = JSON.stringify(currentFilters);
      }

      if (selectedContracts.length > 0) {
        params.contractIds = selectedContracts.map(c => c.id).join(',');
      }

      const response = await axiosInstance.get(`/transactions/team/${teamId}`, {
        params,
        signal: abortControllerRef.current.signal
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (response.data && response.data.transactions) {
        const txList = response.data.transactions;
        const metadata = response.data.metadata || {};
        const totalCount = metadata.totalCount ?? txList.length;
        const totalPagesCalc = metadata.totalPages ?? Math.max(1, Math.ceil(totalCount / 25));

        if (isReset) {
          setTransactions(txList);
        } else {
          setTransactions(prev => [...prev, ...txList]);
        }
        setTotalPages(totalPagesCalc);
        setCurrentPage(page);
        setTotalTransactions(totalCount);
      } else {
        if (isReset) setTransactions([]);
        setTotalPages(1);
        setTotalTransactions(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to fetch transactions');
      if (isReset) setTransactions([]);
      setTotalPages(1);
      setTotalTransactions(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreTransactions(false);
      }
    }
  };
  
  // Fetch users for Users view. isReset = replace list; !isReset = append (Load More). Uses last_visit for time filter.
  const fetchUsersWithDateRange = async (page = 1, searchOverride = null, timeFilterOverride = null, isReset = true, filtersOverride = null) => {
    if (!teamId) {
      setUsers([]);
      setTotalUsers(0);
      setTotalPages(1);
      setLoading(false);
      setLoadingMoreUsers(false);
      return;
    }

    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMoreUsers(true);
      }

      const timeRange = getUsersTimeRangeParams(timeFilterOverride);
      const params = {
        team_id: teamId,
        page: page.toString(),
        limit: '25'
      };
      if (timeRange.from) params.from = timeRange.from;
      if (timeRange.to) params.to = timeRange.to;

      const currentSearchTerm = searchOverride !== null ? searchOverride : searchTerm;
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.search = currentSearchTerm.trim();
      }

      const filtersToUse = Array.isArray(filtersOverride) ? filtersOverride : (appliedModularFiltersUsers || []);
      if (filtersToUse.length > 0) {
        params.modularFilters = JSON.stringify(filtersToUse);
      }

      // React Query cache key for instant back/forward UX
      const usersCacheKey = [
        'activity',
        'users',
        teamId,
        timeRange.from || null,
        timeRange.to || null,
        currentSearchTerm?.trim() || '',
        filtersToUse,
      ];

      // Hydrate list immediately from cache for the first page/reset fetch
      if (isReset && page === 1) {
        const cached = queryClient.getQueryData(usersCacheKey);
        if (cached && typeof cached === 'object') {
          setUsers(cached.users || []);
          setTotalUsers(cached.total || 0);
          setTotalPages(cached.totalPages || 1);
          setCurrentPage(1);
          setLoading(false);
        }
      }

      const response = await axiosInstance.get('/user-identities', {
        params,
        signal: abortControllerRef.current.signal
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (response.data && response.data.success) {
        const fetchedUsers = response.data.users || [];
        const total = response.data.total || 0;
        const totalPagesCalc = response.data.totalPages || Math.max(1, Math.ceil(total / 25));

        if (isReset) {
          setUsers(fetchedUsers);
        } else {
          setUsers(prev => [...prev, ...fetchedUsers]);
        }
        setTotalPages(totalPagesCalc);
        setCurrentPage(page);
        setTotalUsers(total);

        // Cache the first page result for this exact query (tab/filter/search/time range)
        if (page === 1) {
          queryClient.setQueryData(usersCacheKey, {
            users: fetchedUsers,
            total,
            totalPages: totalPagesCalc,
            fetchedAt: Date.now(),
          });
        }
      } else {
        if (isReset) setUsers([]);
        setTotalPages(1);
        setTotalUsers(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
      if (isReset) setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreUsers(false);
      }
    }
  };

  // Fetch groups for a specific group_key tab. isReset = replace list; !isReset = append.
  const fetchGroupsWithDateRange = async (page = 1, searchOverride = null, timeFilterOverride = null, isReset = true, filtersOverride = null) => {
    if (!teamId || !activeGroupTab) {
      setGroups([]);
      setTotalGroups(0);
      setTotalPages(1);
      if (isReset) setLoading(false);
      else setLoadingMoreGroups(false);
      return;
    }

    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) { setLoading(true); setError(null); }
      else setLoadingMoreGroups(true);

      const timeRange = getGroupsTimeRangeParams(timeFilterOverride);
      const reqParams = {
        team_id: teamId,
        group_key: activeGroupTab,
        page: page.toString(),
        limit: '25'
      };
      if (timeRange.from) reqParams.from = timeRange.from;
      if (timeRange.to) reqParams.to = timeRange.to;

      const currentSearch = searchOverride !== null ? searchOverride : groupSearchTerm;
      if (currentSearch && currentSearch.trim()) reqParams.search = currentSearch.trim();

      const filtersToUse = Array.isArray(filtersOverride) ? filtersOverride : (appliedGroupModularFilters || []);
      if (filtersToUse.length > 0) reqParams.modularFilters = JSON.stringify(filtersToUse);

      const groupsCacheKey = [
        'activity',
        'groups',
        teamId,
        activeGroupTab,
        timeRange.from || null,
        timeRange.to || null,
        currentSearch?.trim() || '',
        filtersToUse,
      ];

      if (isReset && page === 1) {
        const cached = queryClient.getQueryData(groupsCacheKey);
        if (cached && typeof cached === 'object') {
          setGroups(cached.groups || []);
          setTotalGroups(cached.total || 0);
          setTotalPages(cached.totalPages || 1);
          setCurrentPage(1);
          setLoading(false);
        }
      }

      const response = await axiosInstance.get('/group-identities', {
        params: reqParams,
        signal: abortControllerRef.current.signal
      });

      if (requestId !== latestRequestRef.current) return;

      if (response.data && response.data.success) {
        const fetchedGroups = response.data.groups || [];
        const total = response.data.total || 0;
        const totalPagesCalc = response.data.totalPages || Math.max(1, Math.ceil(total / 25));

        if (isReset) setGroups(fetchedGroups);
        else setGroups(prev => [...prev, ...fetchedGroups]);
        setTotalPages(totalPagesCalc);
        setCurrentPage(page);
        setTotalGroups(total);

        if (page === 1) {
          queryClient.setQueryData(groupsCacheKey, {
            groups: fetchedGroups,
            total,
            totalPages: totalPagesCalc,
            fetchedAt: Date.now(),
          });
        }
      } else {
        if (isReset) setGroups([]);
        setTotalPages(1);
        setTotalGroups(0);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching groups:', err);
      setError(err.response?.data?.message || 'Failed to fetch groups');
      if (isReset) setGroups([]);
      setTotalPages(1);
      setTotalGroups(0);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreGroups(false);
      }
    }
  };

  // Users filters change: apply dynamically (no Apply button). Validates, updates state, refetches.
  const handleModularFiltersUsers = (filters) => {
    setModularFiltersUsers(filters);
    const noValueOperators = ['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'];
    const validFilters = (filters || []).filter(f => {
      if (!(f.field || f.property) || !f.operator) return false;
      if (!noValueOperators.includes(f.operator)) {
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if ((f.operator === 'between' || f.operator === 'not_between') && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      }
      return true;
    });
    setAppliedModularFiltersUsers(validFilters);
    setCurrentPage(1);
    fetchUsersWithDateRange(1, null, null, true, validFilters);
  };

  // Session filters change: apply dynamically (no Apply button). Validates, updates state, refetches.
  const handleSessionFiltersChange = (filters) => {
    setModularFilters(filters);
    const noValueOperators = ['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'];
    const validFilters = (filters || []).filter(f => {
      if (!(f.field || f.property) || !f.operator) return false;
      if (!noValueOperators.includes(f.operator)) {
        if (f.value === null || f.value === undefined || f.value === '') return false;
        if (Array.isArray(f.value) && f.value.length === 0) return false;
        if ((f.operator === 'between' || f.operator === 'not_between') && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      }
      return true;
    });
    setAppliedModularFilters(validFilters);
    setCurrentPage(1);
    fetchSessionsWithDateRange(1, null, null, true, validFilters);
  };

  // Apply events filters
  const handleApplyEventsFilters = () => {
    // Filter out incomplete filters (missing field, operator, or value)
    // Also handle empty arrays for multiselect
    const validFilters = eventsFilters.filter(f => {
      if (!f.field || !f.operator) return false;
      if (f.value === null || f.value === undefined || f.value === '') return false;
      // For multiselect, ensure array is not empty
      if (Array.isArray(f.value) && f.value.length === 0) return false;
      // For between operator, ensure both values are set
      if (f.operator === 'between' && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      return true;
    });
    
    setAppliedEventsFilters(validFilters);
    setCurrentPage(1);
    setTotalEvents(0); // Reset total when filters change
    
    if (teamId) {
      fetchEvents(1, null, validFilters);
    }
  };

  // Handle applying onchain filters (global filters + per-contract filters stack)
  const handleApplyOnchainFilters = () => {
    const fieldOrProp = (f) => f.field || f.property;
    const validFilters = onchainFilters.filter(f => {
      if (!fieldOrProp(f) || !f.operator) return false;
      if (['is_set', 'is_not_set', 'is_true', 'is_false', 'is_numeric', 'is_not_numeric'].includes(f.operator)) return true;
      if (f.value === null || f.value === undefined || f.value === '') return false;
      if (Array.isArray(f.value) && f.value.length === 0) return false;
      if (f.operator === 'between' && (f.value2 === null || f.value2 === undefined || f.value2 === '')) return false;
      return true;
    });
    setAppliedOnchainFilters(validFilters);
    setCurrentPage(1);
    const combined = [...validFilters, ...(contractPropertyFilters || [])];
    fetchTransactionsWithDateRange(1, true, null, combined.length ? combined : null, null);
  };

  // Fetch sessions (time filter uses start_time). isReset = replace list; !isReset = append (Load More). filtersOverride = use these instead of appliedModularFilters.
  const fetchSessionsWithDateRange = async (page = 1, searchOverride = null, timeFilterOverride = null, isReset = true, filtersOverride = null) => {
    if (!teamId) {
      if (isReset) {
        setSessions([]);
        setTotalSessions(0);
        setTotalPages(1);
        setLoading(false);
      } else {
        setLoadingMoreSessions(false);
      }
      return;
    }
    let requestId = 0;
    try {
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMoreSessions(true);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25'
      });

      const timeRange = getSessionTimeRangeParams(timeFilterOverride);
      if (timeRange.from) params.set('from', timeRange.from);
      if (timeRange.to) params.set('to', timeRange.to);

      const currentSearchTerm = searchOverride !== null ? searchOverride : searchTerm;
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.set('search', currentSearchTerm.trim());
      }

      const filtersToUse = Array.isArray(filtersOverride) ? filtersOverride : (appliedModularFilters || []);
      if (filtersToUse.length > 0) {
        params.set('modularFilters', JSON.stringify(filtersToUse));
      }

      if (teamId) {
        params.set('teamId', teamId);
      }

      const response = await axiosInstance.get(`/sessions?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });
      if (requestId !== latestRequestRef.current) {
        return;
      }

      const newSessions = response.data.sessions || [];
      const total = response.data.totalSessions ?? 0;
      const totalPagesCalc = response.data.totalPages ?? Math.max(1, Math.ceil(total / 25));

      if (isReset) {
        setSessions(newSessions);
      } else {
        setSessions(prev => [...prev, ...newSessions]);
      }
      setTotalPages(totalPagesCalc);
      setCurrentPage(page);
      setTotalSessions(total);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching sessions:', err);
      setError(err.response?.data?.message || 'Failed to fetch sessions');
      if (isReset) setSessions([]);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) setLoading(false);
        else setLoadingMoreSessions(false);
      }
    }
  };

  // Initial data fetch for Offchain (after time filter is set)
  useEffect(() => {
    if (chainContext === 'Offchain' && teamId && sessionTimeFilter) {
      setCurrentPage(1);
      fetchSessionsWithDateRange(1);
    }
  }, [teamId, chainContext, sessionTimeFilter]);

  // Initial data fetch for Events
  useEffect(() => {
    if (chainContext === 'Events' && teamId) {
      setCurrentPage(1);
      fetchEvents(1, null, appliedEventsFilters);
    }
  }, [teamId, chainContext]);

  // Initial data fetch for Onchain (team-level; after time filter is set)
  useEffect(() => {
    if (chainContext === 'Onchain' && teamId && transactionTimeFilter) {
      setCurrentPage(1);
      fetchTransactionsWithDateRange(1, true);
    }
  }, [selectedContracts, chainContext, transactionTimeFilter, teamId]);

  // Initial data fetch for Users (after time filter is set)
  useEffect(() => {
    if (chainContext === 'Users' && userTimeFilter) {
      setCurrentPage(1);
      const userIdParam = new URLSearchParams(location.search).get('userId');
      if (userIdParam) setSearchTerm(userIdParam);
      fetchUsersWithDateRange(1, userIdParam || null, null, true);
    }
  }, [chainContext, userTimeFilter, location.search]);

  // Refetch when onchain filters change (only for Onchain) – bar filters apply dynamically like Events
  useEffect(() => {
    if (chainContext === 'Onchain' && teamId && transactionTimeFilter) {
      setCurrentPage(1);
      fetchTransactionsWithDateRange(1, true);
    }
  }, [onchainFilters, contractPropertyFilters, chainContext]);

  // Load transaction table columns when Onchain
  useEffect(() => {
    if (chainContext === 'Onchain' && selectedTeam) {
      loadTransactionsTableColumns(selectedTeam);
    }
  }, [chainContext, selectedTeam, loadTransactionsTableColumns]);

  // Initialize transaction time filter to last 30 days when Onchain (once) so team transactions show by default
  const transactionTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Onchain' && selectedTeam && !transactionTimeFilterInitialized.current) {
      transactionTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setTransactionTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setTransactionSelectedTimePreset('30d');
    }
    if (chainContext !== 'Onchain') {
      transactionTimeFilterInitialized.current = false;
    }
  }, [chainContext, selectedTeam]);

  // Refetch when modular filters change (only for Offchain)
  useEffect(() => {
    if (chainContext === 'Offchain' && teamId && sessionTimeFilter) {
      setCurrentPage(1);
      fetchSessionsWithDateRange(1);
    }
  }, [appliedModularFilters, chainContext, teamId, sessionTimeFilter]);

  // Load sessions table columns when Offchain
  useEffect(() => {
    if (chainContext === 'Offchain' && selectedTeam) {
      loadSessionsTableColumns(selectedTeam);
    }
  }, [chainContext, selectedTeam, loadSessionsTableColumns]);

  // Load users table columns when Users
  useEffect(() => {
    if (chainContext === 'Users' && selectedTeam) {
      loadUsersTableColumns(selectedTeam);
    }
  }, [chainContext, selectedTeam, loadUsersTableColumns]);

  // Initialize user time filter to last 30 days when Users (once)
  const userTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Users' && selectedTeam && !userTimeFilterInitialized.current) {
      userTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setUserTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setUserSelectedTimePreset('30d');
    }
    if (chainContext !== 'Users') {
      userTimeFilterInitialized.current = false;
    }
  }, [chainContext, selectedTeam]);

  // Fetch group_keys when entering Users context
  useEffect(() => {
    if (chainContext === 'Users' && teamId) {
      const keysCacheKey = ['activity', 'groupKeys', teamId];

      // Instant render from cache (fast back/forward + reduces tab load lag)
      const cached = queryClient.getQueryData(keysCacheKey);
      if (Array.isArray(cached)) {
        setGroupKeys(cached);
      }

      axiosInstance
        .get('/group-identities/keys', { params: { team_id: teamId } })
        .then((r) => {
          const keys = r.data.keys || [];
          setGroupKeys(keys);
          queryClient.setQueryData(keysCacheKey, keys);
        })
        .catch(() => setGroupKeys([]));
    }
    if (chainContext !== 'Users') {
      setGroupKeys([]);
      setActiveGroupTab(null);
    }
  }, [chainContext, teamId]);

  // Persist the selected group tab via URL: ?groupKey=team (etc).
  // Used when navigating back from GroupDetailPage so the UX returns to the groups view.
  useEffect(() => {
    if (chainContext !== 'Users') return;
    const searchParams = new URLSearchParams(location.search);
    const groupKeyParam = (searchParams.get('groupKey') || '').trim();
    if (!groupKeyParam) return;
    // Only activate if this group key exists for the team; otherwise ignore.
    if (Array.isArray(groupKeys) && groupKeys.some((k) => k?.group_key === groupKeyParam)) {
      if (activeGroupTab !== groupKeyParam) {
        handleGroupTabChange(groupKeyParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainContext, location.search, groupKeys]);

  // Distinct custom property keys for the active group_key (Edit columns + table headers)
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && teamId) {
      fetchGroupCustomPropertyKeys();
    } else {
      setGroupCustomPropertyKeysFromApi([]);
    }
  }, [chainContext, activeGroupTab, teamId, fetchGroupCustomPropertyKeys]);

  useEffect(() => {
    if (isGroupColumnModalOpen && chainContext === 'Users' && activeGroupTab && teamId) {
      fetchGroupCustomPropertyKeys();
    }
  }, [isGroupColumnModalOpen, chainContext, activeGroupTab, teamId, fetchGroupCustomPropertyKeys]);

  // Initialize group time filter to last 30 days when a group tab becomes active (once per tab)
  const groupTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && selectedTeam && !groupTimeFilterInitialized.current) {
      groupTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setGroupTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setGroupSelectedTimePreset('30d');
    }
    if (!activeGroupTab) {
      groupTimeFilterInitialized.current = false;
    }
  }, [chainContext, activeGroupTab, selectedTeam]);

  // Fetch groups when activeGroupTab or groupTimeFilter changes
  useEffect(() => {
    if (chainContext === 'Users' && activeGroupTab && groupTimeFilter) {
      setCurrentPage(1);
      fetchGroupsWithDateRange(1, null, null, true);
    }
  }, [chainContext, activeGroupTab, groupTimeFilter]);

  // Initialize session time filter to last 30 days when Offchain (once)
  const sessionTimeFilterInitialized = useRef(false);
  useEffect(() => {
    if (chainContext === 'Offchain' && selectedTeam && !sessionTimeFilterInitialized.current) {
      sessionTimeFilterInitialized.current = true;
      const now = new Date();
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setSessionTimeFilter({
        type: 'preset',
        preset: '30d',
        from: from.toISOString(),
        to: now.toISOString()
      });
      setSessionSelectedTimePreset('30d');
    }
    if (chainContext !== 'Offchain') {
      sessionTimeFilterInitialized.current = false;
    }
  }, [chainContext, selectedTeam]);

  // Update document title when search is active
  useEffect(() => {
    if (searchTerm) {
      document.title = `Search: "${searchTerm}" - Sessions | Cryptique`;
    } else {
      document.title = 'Sessions | Cryptique';
    }
  }, [searchTerm]);

  // Check if team is selected
  if (!selectedTeam) {
    return (
      <div className="flex-grow overflow-y-auto p-6">
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-[#caa968] rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-[#0f172a] font-medium" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Please select a team to view sessions.
              </p>
              <p className="text-xs text-[#475569] mt-1">
                You can select a team from the sidebar or settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For Onchain, we always render the full layout (header + footer) even if no contracts are selected
  // The blank state will be shown in the table component

  return (
    <div className="flex flex-1 overflow-hidden relative">
      {/* Main content area */}
      <div className="flex flex-col flex-grow overflow-hidden">
        {/* Transactions filter bar (Onchain only) - outside scroll so it stays visible */}
        {chainContext === 'Onchain' && (
          <TransactionsFilterBar
            onRefresh={handleTransactionRefresh}
            onTimeFilterChange={handleTransactionTimeFilterChange}
            onSearchChange={handleSearch}
            searchTerm={searchTerm}
            selectedTimePreset={transactionSelectedTimePreset}
            lastRefreshTime={lastTransactionRefreshTime}
            teamId={selectedTeam}
            onEditColumns={() => setIsTransactionColumnModalOpen(true)}
            selectedContracts={selectedContracts}
            onSelectedContractsChange={setSelectedContracts}
            contractPropertyFilters={contractPropertyFilters}
            onContractPropertyFiltersChange={setContractPropertyFilters}
            filters={onchainFilters}
            onFiltersChange={setOnchainFilters}
          />
        )}
        {/* Table - fills remaining space. Offchain/Users: bar then table in p-6 wrapper. Events/Onchain: p-6. */}
        <div className={`flex-1 overflow-y-auto ${chainContext === 'Offchain' || chainContext === 'Users' ? '' : 'p-6'}`}>
          {chainContext === 'Offchain' ? (
            <>
              <SessionsFilterBar
                onRefresh={handleSessionRefresh}
                onTimeFilterChange={handleSessionTimeFilterChange}
                onSearchChange={handleSearch}
                searchTerm={searchTerm}
                selectedTimePreset={sessionSelectedTimePreset}
                lastRefreshTime={lastSessionRefreshTime}
                onEditColumns={() => setIsSessionColumnModalOpen(true)}
                filters={modularFilters}
                onFiltersChange={handleSessionFiltersChange}
                teamId={selectedTeam}
              />
              {/* Table in p-6 wrapper like Events - keeps table from going edge to edge */}
              <div className="p-6">
                <SessionsTable
                  sessions={sessions}
                  loading={loading}
                  loadingMore={loadingMoreSessions}
                  error={error}
                  totalSessions={totalSessions}
                  hasMore={currentPage < totalPages}
                  onLoadMore={handleSessionLoadMore}
                  hasSearchFilter={!!searchTerm}
                  searchTerm={searchTerm}
                  onSearch={handleSearch}
                  searchPlaceholder="Search by session ID or user ID..."
                  sessionIdFromUrl={new URLSearchParams(location.search).get('sessionId')}
                  columnConfig={getSessionsTableColumns()}
                />
              </div>
            </>
          ) : chainContext === 'Events' ? (
            <EventsTable 
              events={events}
              loading={loading}
              error={error}
              currentPage={currentPage}
              totalPages={totalPages}
              totalEvents={totalEvents}
              onPageChange={handlePageChange}
              hasSearchFilter={!!searchTerm}
              searchTerm={searchTerm}
              onSearch={handleSearch}
              searchPlaceholder="Search by event name, session ID or user ID..."
            />
          ) : chainContext === 'Users' ? (
            <>
              {/* Inner tab bar: Users + one per group_key (only when groups exist) */}
              {groupKeys.length > 0 && (
                <div className="flex items-center border-b border-gray-200 bg-white px-6 overflow-x-auto flex-shrink-0">
                  <button
                    onClick={() => handleGroupTabChange(null)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 mr-1 transition-colors ${
                      activeGroupTab === null
                        ? 'border-[#4B57AA] text-[#4B57AA]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    Users
                  </button>
                  {groupKeys.map(({ group_key }) => (
                    <button
                      key={group_key}
                      onClick={() => handleGroupTabChange(group_key)}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 mr-1 transition-colors ${
                        activeGroupTab === group_key
                          ? 'border-[#4B57AA] text-[#4B57AA]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                      style={{ fontFamily: 'Raleway, sans-serif' }}
                    >
                      {group_key
                        .replace(/_id$/, '')
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, c => c.toUpperCase())}
                    </button>
                  ))}

                  {/* Refresh button lives in the tab strip when tabs are shown */}
                  <button
                    type="button"
                    onClick={() => {
                      if (activeGroupTab === null) handleUserRefresh();
                      else handleGroupRefresh();
                    }}
                    className="ml-auto flex items-center gap-2 px-3 py-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors whitespace-nowrap"
                    title={activeGroupTab === null ? 'Refresh users' : 'Refresh groups'}
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              )}

              {activeGroupTab === null ? (
                <>
                  <UsersFilterBar
                    onRefresh={handleUserRefresh}
                    onTimeFilterChange={handleUserTimeFilterChange}
                    onSearchChange={handleSearch}
                    searchTerm={searchTerm}
                    selectedTimePreset={userSelectedTimePreset}
                    lastRefreshTime={lastUserRefreshTime}
                    showTitle={groupKeys.length === 0}
                    showRefresh={groupKeys.length === 0}
                    onEditColumns={() => setIsUserColumnModalOpen(true)}
                    filters={modularFiltersUsers}
                    onFiltersChange={handleModularFiltersUsers}
                    teamId={selectedTeam}
                  />
                  <div className="p-6">
                    <UsersTable
                      users={users}
                      loading={loading}
                      loadingMore={loadingMoreUsers}
                      error={error}
                      totalUsers={totalUsers}
                      hasMore={currentPage < totalPages}
                      onLoadMore={handleUserLoadMore}
                      hasSearchFilter={!!searchTerm}
                      searchTerm={searchTerm}
                      columnConfig={getUsersTableColumns()}
                      onRowClick={handleUserRowClick}
                    />
                  </div>
                </>
              ) : (
                <>
                  <GroupsFilterBar
                    onRefresh={handleGroupRefresh}
                    onTimeFilterChange={(filter) => {
                      setGroupTimeFilter(filter);
                      setGroupSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
                      setCurrentPage(1);
                      setGroups([]);
                      setTotalGroups(0);
                    }}
                    onSearchChange={handleGroupSearchChange}
                    searchTerm={groupSearchTerm}
                    selectedTimePreset={groupSelectedTimePreset}
                    lastRefreshTime={lastGroupRefreshTime}
                    showTitle={false}
                    showRefresh={false}
                    onEditColumns={() => setIsGroupColumnModalOpen(true)}
                    filters={groupModularFilters}
                    onFiltersChange={handleGroupModularFiltersChange}
                    teamId={selectedTeam}
                    groupKey={activeGroupTab}
                  />
                  <div className="p-6">
                    <GroupsTable
                      groups={groups}
                      loading={loading}
                      loadingMore={loadingMoreGroups}
                      error={error}
                      totalGroups={totalGroups}
                      hasMore={currentPage < totalPages}
                      onLoadMore={handleGroupLoadMore}
                      columnConfig={getGroupsTableColumns(activeGroupTab)}
                      onRowClick={handleGroupRowClick}
                      groupKey={activeGroupTab}
                      knownCustomPropKeys={groupAvailableCustomProps}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <OnchainTransactionsTable 
              transactions={transactions}
              loading={loading}
              loadingMore={loadingMoreTransactions}
              error={error}
              totalTransactions={totalTransactions}
              hasMore={currentPage < totalPages}
              onLoadMore={handleTransactionLoadMore}
              columnConfig={getTransactionsTableColumns()}
            />
          )}
        </div>
      </div>

      {/* Transaction column config modal (Onchain only) */}
      {chainContext === 'Onchain' && (
        <TransactionColumnConfigModal
          isOpen={isTransactionColumnModalOpen}
          onClose={() => setIsTransactionColumnModalOpen(false)}
          teamId={selectedTeam}
        />
      )}
      {/* Session column config modal (Offchain only) */}
      {chainContext === 'Offchain' && (
        <SessionColumnConfigModal
          isOpen={isSessionColumnModalOpen}
          onClose={() => setIsSessionColumnModalOpen(false)}
          teamId={selectedTeam}
        />
      )}
      {/* User column config modal (Users only, Users tab) */}
      {chainContext === 'Users' && activeGroupTab === null && (
        <UserColumnConfigModal
          isOpen={isUserColumnModalOpen}
          onClose={() => setIsUserColumnModalOpen(false)}
          teamId={selectedTeam}
        />
      )}
      {/* Group column config modal (Users context, group tab active) */}
      {chainContext === 'Users' && activeGroupTab !== null && (
        <GroupColumnConfigModal
          isOpen={isGroupColumnModalOpen}
          onClose={() => setIsGroupColumnModalOpen(false)}
          teamId={selectedTeam}
          groupKey={activeGroupTab}
          availableCustomProps={groupAvailableCustomProps}
        />
      )}
    </div>
  );
};

export default Sessions;
